import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Disc } from 'lucide-react';

// --- Types ---
export type DialItemType = "kitchen" | "category" | "variant";

export interface DialItem {
  id: string;
  name: string;
  icon?: React.ReactNode; 
  type: DialItemType;
  children?: DialItem[];
  price?: number;
  // ✅ FIX: Explicitly typed 'data' to hold the original item object
  data?: any; 
  // Allow other extra props
  [key: string]: any; 
}

interface ConcentricDialProps {
  data: DialItem[];
  onSelectionComplete: (path: DialItem[]) => void;
  onClose?: () => void; 
}

// --- Constants ---
const RADII = {
  CENTER: 0,
  LEVEL_1: 100,  // Inner (History)
  LEVEL_2: 180, // Middle (History)
  LEVEL_3: 260  // Outer (Active)
};

// --- Helper: Position Calc ---
const getPosition = (index: number, total: number, radius: number) => {
  if (total === 0) return { x: 0, y: 0 };
  const angleIncrement = (2 * Math.PI) / total;
  const angle = angleIncrement * index - (Math.PI / 2); // Start at top
  return {
    x: radius * Math.cos(angle),
    y: radius * Math.sin(angle)
  };
};

// --- Component: Connector Line ---
const ConnectorPath = ({ history, currentRadius }: { history: { items: DialItem[], selectedIndex: number }[], currentRadius: number }) => {
  const center = { x: 0, y: 0 };
  
  const points = history.map((level, lvlIndex) => {
    // Reverse index to map stack depth to ring radius
    const reverseIndex = history.length - 1 - lvlIndex;
    const radius = reverseIndex === 0 ? RADII.LEVEL_2 : RADII.LEVEL_1; 
    return getPosition(level.selectedIndex, level.items.length, radius);
  });

  let d = `M ${center.x} ${center.y}`;
  points.forEach(p => { d += ` L ${p.x} ${p.y}`; });

  return (
    <svg className="absolute top-0 left-0 w-full h-full pointer-events-none z-0" style={{ transform: 'translate(50%, 50%)', overflow: 'visible' }}>
      <defs>
        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <motion.path
        d={d}
        fill="none"
        stroke="#06b6d4" // Cyan-500
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter="url(#glow)"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      />
    </svg>
  );
};

// --- Main Component ---
const ConcentricDial: React.FC<ConcentricDialProps> = ({ data, onSelectionComplete, onClose }) => {
  const [history, setHistory] = useState<{ items: DialItem[], selectedIndex: number }[]>([]);
  const [activeItems, setActiveItems] = useState<DialItem[]>(data);

  const handleSelection = (item: DialItem, index: number) => {
    if (item.children && item.children.length > 0) {
      // DRILL DOWN
      setHistory(prev => [...prev, { items: activeItems, selectedIndex: index }]);
      setActiveItems(item.children);
    } else {
      // LEAF NODE
      const fullPath = [...history.map(h => h.items[h.selectedIndex]), item];
      onSelectionComplete(fullPath);
    }
  };

  const handleBack = () => {
    if (history.length === 0) {
        if (onClose) onClose();
        return;
    }
    const prevLevel = history[history.length - 1];
    setActiveItems(prevLevel.items);
    setHistory(prev => prev.slice(0, -1));
  };

  return (
    <div className="relative w-[600px] h-[600px] flex items-center justify-center font-mono">
      
      {/* Path Trace */}
      <ConnectorPath history={history} currentRadius={RADII.LEVEL_3} />

      {/* 1. CENTRAL HUB (Back/Close Button) */}
      <motion.div 
        className="absolute z-50 w-24 h-24 rounded-full bg-gray-900 border-2 border-cyan-500 shadow-[0_0_30px_rgba(6,182,212,0.6)] flex items-center justify-center cursor-pointer hover:bg-gray-800 transition-colors group"
        onClick={handleBack}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
         <div className="text-cyan-400 text-xs text-center font-bold tracking-widest group-hover:text-cyan-200 flex flex-col items-center">
           {history.length > 0 ? <ArrowLeft size={20} /> : <span className="text-[10px]">RUCHIRA</span>}
           <span className="text-[9px] mt-1">{history.length > 0 ? "BACK" : (onClose ? "EXIT" : "HUB")}</span>
         </div>
      </motion.div>

      {/* 2. HISTORY RINGS (Inactive, Nested) */}
      {history.map((level, lvlIndex) => {
        const reverseIndex = history.length - 1 - lvlIndex;
        const radius = reverseIndex === 0 ? RADII.LEVEL_2 : RADII.LEVEL_1;
        const opacity = reverseIndex === 0 ? 0.5 : 0.2;

        return (
          <React.Fragment key={`history-${lvlIndex}`}>
             {/* Ring Border */}
             <motion.div 
               className="absolute border border-cyan-800/50 rounded-full"
               initial={{ width: RADII.LEVEL_3 * 2, height: RADII.LEVEL_3 * 2 }}
               animate={{ width: radius * 2, height: radius * 2, opacity: opacity }}
               transition={{ duration: 0.6, type: "spring" }}
             />
             
             {/* Selected Item Only */}
             {level.items.map((item, idx) => {
               if (idx !== level.selectedIndex) return null;
               const pos = getPosition(idx, level.items.length, radius);
               return (
                 <motion.div
                   key={`hist-item-${item.id}`}
                   className="absolute w-12 h-12 -ml-6 -mt-6 rounded-full bg-cyan-900 border border-cyan-500 flex items-center justify-center shadow-[0_0_10px_rgba(6,182,212,0.8)] z-10"
                   initial={{ x: 0, y: 0 }} 
                   animate={{ x: pos.x, y: pos.y }}
                   transition={{ duration: 0.6, type: "spring" }}
                 >
                    <div className="text-cyan-200 scale-75">{item.icon || <Disc />}</div>
                 </motion.div>
               );
             })}
          </React.Fragment>
        );
      })}

      {/* 3. ACTIVE RING (Outermost) */}
      <div className="absolute z-20 w-full h-full pointer-events-none">
          {/* Active Ring Border */}
          <motion.div 
               className="absolute border border-cyan-500/30 rounded-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
               style={{ width: RADII.LEVEL_3 * 2, height: RADII.LEVEL_3 * 2 }}
               initial={{ scale: 0.8, opacity: 0 }}
               animate={{ scale: 1, opacity: 1 }}
               transition={{ duration: 0.5 }}
          />

          {/* Active Items */}
          <AnimatePresence mode='popLayout'>
          {activeItems.map((item, index) => {
            const pos = getPosition(index, activeItems.length, RADII.LEVEL_3);
            
            return (
              <motion.div
                key={item.id}
                className="absolute top-1/2 left-1/2 pointer-events-auto"
                style={{ x: pos.x, y: pos.y }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{ delay: index * 0.05, type: "spring", stiffness: 200 }}
              >
                <div 
                  className="relative -ml-10 -mt-10 w-20 h-20 group cursor-pointer"
                  onClick={() => handleSelection(item, index)}
                >
                  {/* Bubble */}
                  <div className="w-20 h-20 rounded-full bg-gray-900/90 backdrop-blur-md border-2 border-cyan-500/50 group-hover:border-cyan-400 group-hover:bg-cyan-950/80 shadow-[0_0_15px_rgba(6,182,212,0.3)] group-hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] flex items-center justify-center transition-all duration-300">
                    <div className="text-cyan-400 drop-shadow-[0_0_5px_rgba(6,182,212,0.8)] transform group-hover:scale-110 transition-transform">
                      {item.icon}
                    </div>
                  </div>

                  {/* Label */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-32 text-center pointer-events-none">
                    <span className="text-[10px] uppercase tracking-widest text-cyan-100 font-bold bg-black/60 px-2 py-1 rounded border border-cyan-900/50 backdrop-blur-sm shadow-lg">
                      {item.name}
                    </span>
                    {item.price && (
                      <div className="text-xs text-cyan-300 font-bold mt-1 bg-black/40 inline-block px-1 rounded">₹{item.price}</div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
          </AnimatePresence>
      </div>

    </div>
  );
};

export default ConcentricDial;